<script setup lang="ts">
import { Filter, Search } from "@element-plus/icons-vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  ContactActivityDto,
  LoginAccountInSysOrgDto,
  OrgWideHubMessage,
  SysOrgRoleName,
  WorkflowDto,
  WorkflowStatus,
} from "~/api";
import ContactActivityTable from "~/Compliance/components/ContactActivityTable.vue";
import WorkflowResponseViewer from "~/Compliance/components/WorkflowResponseViewer.vue";
import RefreshButton from "~/components/RefreshButton.vue";
import { useOrgWideHub } from "~/composables/useOrgWideHub";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { contactHistoryActivityTypeOptions } from "~/YYC_Housing/utils/hmbUtils";

// Assignable activity types for "Assigned to anyone" filter
const assignableActivityTypes = new Set([
  "MeetingActivity",
  "TaskActivity",
  "On Hold Task",
]);

const loading = ref(false);
const route = useRoute();
const workflowDefinitionGuid = computed<string>(() => {
  if (route.meta.workflowDefinitionGuid) {
    return route.meta.workflowDefinitionGuid as string;
  }
  const name = (route.meta.entityName as string) ?? "Contact Activity";
  const wdList = orgInfoStore.MyOrgInfo?.workflowDefinitionDtos ?? [];
  return wdList.find((wd) => wd.name === name)?.guid ?? "";
});

const items = ref<ContactActivityDto[]>([]);
const currentPage = ref(1);
const pageSize = ref(50);
const totalCount = ref(0);
const selectedRow = ref<WorkflowDto | undefined>();
const drawerOpen = ref(false);
// Deep-link: form to auto-focus within the opened workflow (from ?formResponseGuid).
const focusFormResponseGuid = ref<string | null>(null);
const myInfoStore = useMyInfoStore();
const loginAccountsStore = useLoginAccountsStore();
const orgInfoStore = useOrgInfoStore();
const orgWideHub = useOrgWideHub();

const archivedWorkflows = ref(false);

const statusList = ref<WorkflowStatus[]>([]);

// ── Track whether the last search used "all" (no filters) or "with filters" ──
type SearchMode = "all" | "filtered" | null;
const lastSearchMode = ref<SearchMode>(null);

// ── Unified search ──
const searchValue = ref("");

type SearchField = "name" | "appnum" | "email" | "phone" | "explicit";

/**
 * Detects prefix-based field overrides: "Name:", "Email:", "App:", "Phone:"
 * Returns the field and the stripped value.
 *
 * Rules:
 * - Explicit prefixes (Name:/Email:/App:/Phone:) force the field.
 * - @ auto-detect:
 *   - "@ abc"  → search email containing "abc"  (space after @ → strip @ and trim)
 *   - "@abc"   → search email containing "@abc" (no space → include @ in term)
 * - Without a prefix, auto-detect by inspecting the value with quotes stripped
 * - Quoted values are passed through as-is;
 *   the backend interprets the surrounding quotes as exact-word match.
 */
const parseSearchInput = (
  raw: string,
): { field: SearchField; value: string } => {
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();

  // ── Explicit prefix overrides ──────────────────────────────────────────
  if (lower.startsWith("name:")) {
    return { field: "name", value: trimmed.slice(5).trim() };
  }
  if (lower.startsWith("email:")) {
    const val = trimmed.slice(6).trim();
    return { field: "email", value: val };
  }
  if (lower.startsWith("app:")) {
    return { field: "appnum", value: trimmed.slice(4).trim() };
  }
  if (lower.startsWith("phone:")) {
    return { field: "phone", value: trimmed.slice(6).trim() };
  }

  // ── Auto-detect ────────────────────────────────────────────────────────
  if (!trimmed) return { field: "name", value: "" };

  // Strip surrounding quotes
  const inner =
    trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2
      ? trimmed.slice(1, -1).trim()
      : trimmed;

  // @ auto-detect:
  //   "@ abc"  → field=email, value="abc"   (space after @ means @ is just the trigger)
  //   "@abc"   → field=email, value="@abc"  (no space means @ is part of the search term)
  if (inner.startsWith("@ ")) {
    const term = inner.slice(2).trim();
    return { field: "email", value: term };
  }
  if (inner.includes("@")) {
    return { field: "email", value: trimmed };
  }

  // Parentheses are an unambiguous phone indicator
  if (/[()]/.test(inner)) return { field: "phone", value: trimmed };

  const digits = inner.replace(/\D/g, "");

  if (/^\d+$/.test(inner)) {
    // Pure digits: app numbers are 6 digits or fewer; 7+ digits must be a phone number.
    return inner.length >= 7
      ? { field: "phone", value: trimmed }
      : { field: "appnum", value: trimmed };
  }

  // Non-pure-digit string with 7+ digits → phone number segment
  if (digits.length >= 7) return { field: "phone", value: trimmed };

  return { field: "name", value: trimmed };
};

const parsedSearch = computed(() => parseSearchInput(searchValue.value));

const detectedSearchField = computed<SearchField>(
  () => parsedSearch.value.field,
);

const searchFieldLabel = computed<string>(() => {
  const map: Record<SearchField, string> = {
    name: "Searching by name",
    appnum: "Searching by app #",
    email: "Searching by email",
    phone: "Searching by phone",
    explicit: "",
  };
  return map[detectedSearchField.value] ?? "";
});

// ── Quick filter ──
type QuickFilter = "mine" | "myOrg" | "anyone" | "custom";
const quickFilter = ref<QuickFilter>("mine");

// ── Person filter (Custom mode) ──
const selectedUserGuids = ref<string[]>([]);
type PersonInclude = "assigned" | "created" | "both";
const personInclude = ref<PersonInclude>("both"); // Default: Assigned or Created

const customPersonWarning = computed(() => {
  return quickFilter.value === "custom" && selectedUserGuids.value.length === 0;
});

// ── Activity type filter ──
const selectedActivityTypes = ref<string[]>([]);

// ── Activity status filter ──
const selectedStatuses = ref<string[]>([]);

// ── Date range filter ──
type DateField = "all" | "created" | "due" | "interaction";
const dateField = ref<DateField>("all"); // Default: All
const dateRange = ref<[Date, Date] | null>(null); // Default: null (no date filter)

const dateRangeShortcuts = [
  {
    text: "Today",
    value: () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      return [start, new Date()] as [Date, Date];
    },
  },
  {
    text: "Yesterday",
    value: () => {
      const start = new Date();
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setHours(23, 59, 59, 999);
      return [start, end] as [Date, Date];
    },
  },
  {
    text: "Tomorrow",
    value: () => {
      const start = new Date();
      start.setDate(start.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setHours(23, 59, 59, 999);
      return [start, end] as [Date, Date];
    },
  },
  {
    text: "Last 7 Days",
    value: () => {
      const start = new Date();
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return [start, new Date()] as [Date, Date];
    },
  },
  {
    text: "Next 7 Days",
    value: () => {
      const end = new Date();
      end.setDate(end.getDate() + 7);
      end.setHours(23, 59, 59, 999);
      return [new Date(), end] as [Date, Date];
    },
  },
  {
    text: "Last 30 Days",
    value: () => {
      const start = new Date();
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return [start, new Date()] as [Date, Date];
    },
  },
  {
    text: "Next 30 Days",
    value: () => {
      const end = new Date();
      end.setDate(end.getDate() + 30);
      end.setHours(23, 59, 59, 999);
      return [new Date(), end] as [Date, Date];
    },
  },
  {
    text: "Last 90 Days",
    value: () => {
      const start = new Date();
      start.setDate(start.getDate() - 90);
      start.setHours(0, 0, 0, 0);
      return [start, new Date()] as [Date, Date];
    },
  },
  {
    text: "Next 90 Days",
    value: () => {
      const end = new Date();
      end.setDate(end.getDate() + 90);
      end.setHours(23, 59, 59, 999);
      return [new Date(), end] as [Date, Date];
    },
  },
];

// ── Filters panel toggle ──
const filtersOpen = ref(false);

// ── Dropdown data ──
const staffMembers = ref<LoginAccountInSysOrgDto[]>([]);

// ── Sorted staff members for custom person filter ──
// Current user first, then alphabetical by loginName || email
const sortedStaffMembers = computed(() => {
  const currentUserGuid = myInfoStore.userInfo.userGuid;
  const currentUser = staffMembers.value.find(
    (m) => m.userGuid === currentUserGuid,
  );
  const otherMembers = staffMembers.value.filter(
    (m) => m.userGuid !== currentUserGuid,
  );

  // Sort other members alphabetically
  otherMembers.sort((a, b) => {
    const aName = (a.loginName || a.email || "Unknown").toLocaleLowerCase();
    const bName = (b.loginName || b.email || "Unknown").toLocaleLowerCase();

    return aName.localeCompare(bName);
  });

  // Return current user first, then sorted others
  return currentUser ? [currentUser, ...otherMembers] : otherMembers;
});

const hasSearched = ref(false);

// Workflow definitions whose ContactActivity rows surface in Contact History.
// Each applicant interaction (contact logs, holds, cancellations, intake offer/strike
// actions) creates a ContactActivity linked to one of these definitions.
const contactHistoryDefinitionNames = [
  "Contact Activity",
  "On Hold",
  "Cancel Application",
  "Move Back To Review",
  "Offer Form",
  "No Offer Form",
  "Cancel No Offer Form",
  "1 Strike Form",
  "3 Strike Form",
  "Legacy Interaction",
  "Legacy: No Offer",
  "Legacy: Offer",
];

const activityTypeOptions = computed(() => {
  return contactHistoryActivityTypeOptions.map((opt) => ({
    ...opt,
    disabled:
      quickFilter.value === "anyone" &&
      personInclude.value === "assigned" &&
      !assignableActivityTypes.has(opt.value),
  }));
});

// ── Dynamic "Include" label based on quick filter ──
const includeOptions = computed(() => {
  switch (quickFilter.value) {
    case "mine":
      return [
        { value: "assigned", label: "Assigned to me" },
        { value: "both", label: "Assigned or Created by me" },
        { value: "created", label: "Created by me" },
      ];
    case "myOrg":
      return [
        { value: "assigned", label: "Assigned to My Org" },
        { value: "both", label: "Assigned or Created by My Org" },
        { value: "created", label: "Created by My Org" },
      ];
    case "custom":
      return [
        { value: "assigned", label: "Assigned to people" },
        { value: "both", label: "Assigned or Created by people" },
        { value: "created", label: "Created by people" },
      ];
    default: // "anyone"
      return [
        { value: "assigned", label: "Assigned to anyone" },
        { value: "both", label: "Assigned or Created by anyone" },
        { value: "created", label: "Created by anyone" },
      ];
  }
});

// ── Active filter badge count ──
// Badge on the "Advanced Filters" toggle — counts only what's hidden inside
// the collapsed panel. The always-visible ownership controls are excluded.
const activeFilterCount = computed(() => {
  let count = 0;
  // Activity types
  count += selectedActivityTypes.value.length;
  // Statuses
  count += selectedStatuses.value.length;
  // Date range
  if (dateRange.value !== null) count++;
  // Date type
  if (dateField.value !== "all") count++;
  return count;
});

// "Clear Filters" also resets the ownership quick-bar, so it stays enabled
// whenever that deviates from the defaults clearFilters() restores.
const hasActiveFilters = computed(
  () =>
    activeFilterCount.value > 0 ||
    quickFilter.value !== "mine" ||
    selectedUserGuids.value.length > 0 ||
    personInclude.value !== "both",
);

const clearFilters = () => {
  quickFilter.value = "mine";
  selectedUserGuids.value = [];
  personInclude.value = "both";
  selectedActivityTypes.value = [];
  selectedStatuses.value = [];
  dateRange.value = null;
  dateField.value = "all";
  if (hasSearched.value) {
    searchActivities(true, lastSearchMode.value ?? "filtered");
  }
};

/** Clear only the search input, keep all filters */
const clearSearch = () => {
  searchValue.value = "";
};

const removeActivityType = (type: string) => {
  selectedActivityTypes.value = selectedActivityTypes.value.filter(
    (t) => t !== type,
  );
};

const removeStatus = (code: string) => {
  selectedStatuses.value = selectedStatuses.value.filter((s) => s !== code);
};

// Compute the effective date range from the date picker
const effectiveDateRange = computed<[Date, Date] | null>(() => {
  return dateRange.value ?? null;
});

// Compute user GUIDs for the API based on quick filter
const effectiveUserGuids = computed<string[]>(() => {
  switch (quickFilter.value) {
    case "mine":
      return [myInfoStore.userInfo.userGuid];
    case "custom":
      return selectedUserGuids.value;
    case "myOrg": {
      const currentUser = staffMembers.value.find(
        (m) => m.userGuid === myInfoStore.userInfo.userGuid,
      );
      const homeOrg = currentUser?.extraInfo?.homeHousingOrgGuid;
      if (!homeOrg) {
        return [myInfoStore.userInfo.userGuid];
      }
      return staffMembers.value
        .filter((m) => m.extraInfo?.homeHousingOrgGuid === homeOrg)
        .map((m) => m.userGuid);
    }
    case "anyone":
    default:
      return [];
  }
});

// Workflow definitions to search — resolve the configured names to GUIDs,
// skipping any that aren't present in this org.
const effectiveWorkflowDefinitionGuids = computed<string[]>(() => {
  const byName = new Map(
    (orgInfoStore.MyOrgInfo?.workflowDefinitionDtos ?? []).map((wd) => [
      wd.name,
      wd.guid,
    ]),
  );
  return contactHistoryDefinitionNames
    .map((name) => byName.get(name))
    .filter((guid): guid is string => !!guid);
});

const statusLabels = computed(() => {
  return (
    statusList.value
      ?.map((s) => s.label)
      .filter((label): label is string => label !== null) ?? []
  );
});

/** Group statuses by workflowDefinitionName for the dropdown */
type StatusGroup = { wdName: string; statuses: WorkflowStatus[] };
const groupedStatuses = computed<StatusGroup[]>(() => {
  const groups = new Map<string, WorkflowStatus[]>();
  for (const s of statusList.value ?? []) {
    const key = s.workflowDefinitionName ?? "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }
  return Array.from(groups.entries()).map(([wdName, statuses]) => ({
    wdName,
    statuses,
  }));
});

/** Look up the label for a status code */
const statusLabelByCode = (code: string): string => {
  const s = statusList.value?.find((st) => st.code === code);
  return s?.label ?? code;
};

// ── Chip helpers ────────────────────────────────────────────────────────────

/** Label for the quick filter chip */
const quickFilterLabel = computed(() => {
  const map: Record<string, string> = {
    mine: "Mine",
    myOrg: "My Org",
    anyone: "Anyone",
    custom: "Custom",
  };
  return map[quickFilter.value] ?? quickFilter.value;
});

/** Label for the current include option chip */
const includeLabel = computed(
  () =>
    includeOptions.value.find((o) => o.value === personInclude.value)?.label ??
    personInclude.value,
);

/** Labels for selected custom users */
const selectedUserLabels = computed(() =>
  selectedUserGuids.value.map((guid) => {
    const m = staffMembers.value.find((s) => s.userGuid === guid);
    return {
      guid,
      label: m?.loginName || m?.email || guid,
    };
  }),
);

const removeUser = (guid: string) => {
  selectedUserGuids.value = selectedUserGuids.value.filter((g) => g !== guid);
};

/** Formatted date range label */
const dateRangeLabel = computed(() => {
  if (!dateRange.value) return null;
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(dateRange.value[0])} – ${fmt(dateRange.value[1])}`;
});

const removeDateRange = () => {
  dateRange.value = null;
};

/** Label for the date type chip */
const dateTypeLabel = computed(() => {
  const map: Record<string, string> = {
    all: "All Dates",
    interaction: "Interaction Date",
    due: "Due Date",
    created: "Create Date",
  };
  return map[dateField.value] ?? dateField.value;
});

const removeDateType = () => {
  dateField.value = "all";
};

/**
 * Core search function.
 * @param resetPage - reset to page 1 if true
 * @param mode - "all" ignores all filters; "filtered" applies all filters
 */
const searchActivities = async (
  resetPage = true,
  mode: SearchMode = "filtered",
) => {
  if (resetPage) currentPage.value = 1;
  loading.value = true;
  hasSearched.value = true;
  lastSearchMode.value = mode;

  try {
    const { field, value: val } = parsedSearch.value;

    let contactName: string | undefined;
    let applicationNumber: string | undefined;
    let email: string | undefined;
    let phone: string | undefined;

    if (val) {
      switch (field) {
        case "name":
          contactName = val;
          break;
        case "appnum":
          applicationNumber = val;
          break;
        case "email":
          email = val;
          break;
        case "phone":
          phone = val;
          break;
      }
    }

    // When "Search All" — pass ignoreFilters flag; skip person/date/type/status filters
    const ignoreFilters = mode === "all";

    // Determine user GUID filters for the API (only when filters are applied)
    let apiCreatedByUserGuids: string[] | undefined;
    let apiAssignedToUserGuids: string[] | undefined;

    if (!ignoreFilters) {
      const guids = effectiveUserGuids.value;
      apiCreatedByUserGuids =
        guids.length > 0 &&
        (personInclude.value === "created" || personInclude.value === "both")
          ? guids
          : undefined;

      apiAssignedToUserGuids =
        guids.length > 0 &&
        (personInclude.value === "assigned" || personInclude.value === "both")
          ? guids
          : undefined;
    }

    // Compute date range for API (only when filters applied and date range set)
    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    let activeDateField: string | undefined;

    if (!ignoreFilters) {
      const range = effectiveDateRange.value;
      if (range) {
        const from = new Date(range[0]);
        from.setHours(0, 0, 0, 0);
        const to = new Date(range[1]);
        to.setHours(23, 59, 59, 999);
        dateFrom = from.toISOString();
        dateTo = to.toISOString();
      }
      // Only pass dateField if it's not "all"
      activeDateField = dateField.value !== "all" ? dateField.value : undefined;
    }

    // Status codes (only when filters applied)
    const statusCodesForApi =
      !ignoreFilters && selectedStatuses.value.length > 0
        ? selectedStatuses.value.join(",")
        : undefined;

    // Activity types (only when filters applied)
    let activityTypesForApi: string | undefined;
    if (!ignoreFilters) {
      if (
        quickFilter.value === "anyone" &&
        personInclude.value === "assigned"
      ) {
        // When "Assigned to Anyone", only show assignable activities
        const hadSelectedActivityTypes = selectedActivityTypes.value.length > 0;
        const selectedAssignable = selectedActivityTypes.value.filter((type) =>
          assignableActivityTypes.has(type),
        );

        if (selectedAssignable.length !== selectedActivityTypes.value.length) {
          selectedActivityTypes.value = selectedAssignable;
        }

        if (selectedAssignable.length > 0) {
          activityTypesForApi = selectedAssignable.join(",");
        } else if (hadSelectedActivityTypes) {
          activityTypesForApi = undefined;
        } else {
          activityTypesForApi = Array.from(assignableActivityTypes).join(",");
        }
      } else {
        // Normal filtering
        activityTypesForApi =
          selectedActivityTypes.value.length > 0
            ? selectedActivityTypes.value.join(",")
            : undefined;
      }
    }

    // Pagination
    const skip = (currentPage.value - 1) * pageSize.value;

    const result = await apiClient.contactActivity.search(
      myInfoStore.currentSysOrgGuid,
      effectiveWorkflowDefinitionGuids.value,
      archivedWorkflows.value,
      undefined, // entityGuid
      contactName,
      applicationNumber, // housingApplicationSearch — reused for app# lookup
      apiCreatedByUserGuids,
      apiAssignedToUserGuids,
      statusCodesForApi,
      activityTypesForApi,
      undefined, // direction
      undefined, // isImportant
      dateFrom,
      dateTo,
      activeDateField,
      email,
      phone,
      skip,
      pageSize.value,
      ignoreFilters,
    );

    items.value = result.items ?? [];
    statusList.value = result.statusList ?? [];
    totalCount.value = result.totalCount ?? 0;
  } finally {
    loading.value = false;
  }
};

const refreshList = async () => {
  await searchActivities(false, lastSearchMode.value ?? "filtered");
};

const handlePageChange = async (page: number) => {
  currentPage.value = page;
  await searchActivities(false, lastSearchMode.value ?? "filtered");
};

const handleSizeChange = async (size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
  await searchActivities(false, lastSearchMode.value ?? "filtered");
};

const loadDropdownData = async () => {
  await loginAccountsStore.load(false, SysOrgRoleName.MEMBER);
  staffMembers.value = loginAccountsStore.getList(
    SysOrgRoleName.MEMBER,
  ) as LoginAccountInSysOrgDto[];
};

/** Convert a ContactActivityDto to a minimal WorkflowDto for the WorkflowResponseViewer */
const toWorkflowDto = (activity: ContactActivityDto): WorkflowDto => ({
  guid: activity.workflowGuid ?? activity.guid,
  workflowStatus: activity.workflowStatus,
  workflowName: activity.workflowName,
  workflowArchivedAt: activity.workflowArchivedAt ?? "",
  workflowDefinitionName: activity.workflowDefinitionName ?? "",
  workflowDefinitionGuid: activity.workflowDefinitionGuid ?? "",
  ownerName: activity.contactName ?? "",
  ownerGuid: activity.contactEntityGuid ?? null,
  createdByUserGuid: activity.createdByUserGuid ?? null,
  answersToShow: {},
  updatedAt: activity.createdAt ?? "",
  linkedHousingApplicationName: activity.linkedHousingApplicationName ?? null,
  linkedHousingApplicationGuid: activity.linkedHousingApplicationGuid ?? null,
});

const showActivity = (activity: ContactActivityDto) => {
  selectedRow.value = toWorkflowDto(activity);
  drawerOpen.value = true;
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
  focusFormResponseGuid.value = null;
};

const workflowUpdated = (_workflow: WorkflowDto) => {
  searchActivities(false, lastSearchMode.value ?? "filtered");
};

const workflowListNeedsRefresh = (msg: OrgWideHubMessage) => {
  if (
    hasSearched.value &&
    msg.workflowDefinitionGuid_RefreshList &&
    (workflowDefinitionGuid.value === msg.workflowDefinitionGuid_RefreshList ||
      effectiveWorkflowDefinitionGuids.value.includes(
        msg.workflowDefinitionGuid_RefreshList,
      ))
  ) {
    refreshList();
  }
};

// Auto-search when quick filter or person include changes
watch(
  [quickFilter, personInclude],
  ([newQuickFilter, newPersonInclude], [oldQuickFilter, _oldPersonInclude]) => {
    if (newQuickFilter === "custom" && oldQuickFilter !== "custom") {
      // Clear results immediately only when transitioning into Custom mode
      items.value = [];
      totalCount.value = 0;
      // If there are already selected users and we've searched before, trigger search
      if (selectedUserGuids.value.length > 0 && hasSearched.value) {
        searchActivities(true, "filtered");
      }
    } else if (newQuickFilter !== "custom") {
      // When "anyone" and "assigned", filter out non-assignable activity types from selection
      if (newQuickFilter === "anyone" && newPersonInclude === "assigned") {
        selectedActivityTypes.value = selectedActivityTypes.value.filter(
          (type) => assignableActivityTypes.has(type),
        );
      }
      searchActivities(true, "filtered");
    }
  },
);

// Auto-search when custom user selection changes
watch(selectedUserGuids, () => {
  if (
    quickFilter.value === "custom" &&
    hasSearched.value &&
    selectedUserGuids.value.length > 0
  ) {
    searchActivities(true, "filtered");
  } else if (selectedUserGuids.value.length === 0) {
    // If no users are selected in custom mode, clear results
    items.value = [];
    totalCount.value = 0;
  }
});

// Auto-search when activity type selection changes
watch(selectedActivityTypes, () => {
  if (hasSearched.value) {
    searchActivities(true, "filtered");
  }
});

// Auto-search when archived toggle changes
watch(archivedWorkflows, () => {
  if (hasSearched.value) {
    searchActivities(true, "filtered");
  }
});

// Auto-search when status selection changes
watch(selectedStatuses, () => {
  if (hasSearched.value) {
    searchActivities(true, "filtered");
  }
});

// Auto-search when person include mode changes
watch(personInclude, () => {
  if (hasSearched.value) {
    searchActivities(true, "filtered");
  }
});

// Auto-search when date filters change
watch([dateField, dateRange], () => {
  if (hasSearched.value) {
    searchActivities(true, "filtered");
  }
});

onMounted(async () => {
  await Promise.all([orgInfoStore.load(), myInfoStore.load()]);

  if (!workflowDefinitionGuid.value) {
    showError("No workflow definition GUID provided in route meta.");
    return;
  }

  await loadDropdownData();

  quickFilter.value = "mine";
  await searchActivities(true, "filtered");

  // Deep-link: ?workflowGuid=X (+ optional formResponseGuid) opens that workflow directly.
  // The viewer fetches the full workflow off its guid, so a minimal WorkflowDto is enough.
  const workflowGuid = route.query.workflowGuid as string | undefined;
  if (workflowGuid) {
    focusFormResponseGuid.value =
      (route.query.formResponseGuid as string | undefined) ?? null;
    selectedRow.value = { guid: workflowGuid } as WorkflowDto;
    drawerOpen.value = true;
  }

  await orgWideHub.ensure();
  await orgWideHub.on<OrgWideHubMessage>(
    "WorkflowsUpdated",
    workflowListNeedsRefresh,
  );
});

onUnmounted(() => {
  orgWideHub.off("WorkflowsUpdated", workflowListNeedsRefresh);
});
</script>

<template>
  <div class="ContactActivityList">
    <!-- ── Compact search + filter bar ── -->
    <div class="search-bar">
      <!-- Main row -->
      <div class="search-bar__main">
        <!-- ── Search zone ── -->
        <div class="search-bar__zone search-bar__zone--search">
          <span class="search-bar__zone-label">Search</span>
          <!-- Search input -->
          <el-input
            v-model="searchValue"
            class="search-bar__input"
            placeholder="Search by name, app #, email, or phone..."
            style="width: 360px"
            clearable
            :prefix-icon="Search"
            @keyup.enter="() => searchActivities(true, 'all')"
          >
            <template #suffix>
              <span
                class="search-bar__detected-type"
                :data-field="detectedSearchField"
              >
                {{ searchValue ? searchFieldLabel : "" }}
              </span>
            </template>
          </el-input>

          <!-- Search All button -->
          <el-button
            type="primary"
            size="small"
            :loading="loading && lastSearchMode === 'all'"
            @click="() => searchActivities(true, 'all')"
          >
            Search All
          </el-button>

          <!-- Search with Filters button -->
          <el-button
            class="search-with-filters"
            type="primary"
            plain
            size="small"
            :loading="loading && lastSearchMode === 'filtered'"
            @click="() => searchActivities(true, 'filtered')"
          >
            Search with Filters
          </el-button>

          <!-- Clear Search button -->
          <el-button
            size="small"
            plain
            :disabled="!searchValue"
            @click="clearSearch"
          >
            Clear Search
          </el-button>
        </div>

        <!-- ── Filters zone ── -->
        <div class="search-bar__zone search-bar__zone--filters">
          <span class="search-bar__zone-label">Filters</span>

          <!-- Person filter group -->
          <div class="filter-group-inline filter-group-shaded">
            <!-- Quick filter: Mine / My Org / Anyone / Custom -->
            <el-radio-group
              v-model="quickFilter"
              size="small"
              class="search-bar__quick-filter"
            >
              <el-radio-button value="mine">Mine</el-radio-button>
              <el-radio-button value="myOrg">My Org</el-radio-button>
              <el-radio-button value="anyone">Anyone</el-radio-button>
              <el-radio-button value="custom">Custom</el-radio-button>
            </el-radio-group>

            <!-- Custom Person Multi-Select -->
            <div v-if="quickFilter === 'custom'" class="filter-inline-item">
              <el-select
                v-model="selectedUserGuids"
                placeholder="Select people"
                clearable
                multiple
                filterable
                size="small"
                style="width: 180px"
                collapse-tags
                :max-collapse-tags="1"
                :class="{ 'select-warning': customPersonWarning }"
              >
                <el-option
                  v-for="member in sortedStaffMembers"
                  :key="member.userGuid"
                  :label="member.loginName || member.email || 'Unknown'"
                  :value="member.userGuid"
                />
              </el-select>
            </div>

            <!-- Include: dynamic label based on quick filter -->
            <div class="filter-inline-item">
              <el-select
                v-model="personInclude"
                style="width: 225px"
                size="small"
                :disabled="
                  quickFilter === 'custom' && selectedUserGuids.length === 0
                "
              >
                <el-option
                  v-for="opt in includeOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
            </div>
          </div>

          <div class="search-bar__divider" style="margin: 0 4px" />

          <!-- Advanced Filters toggle -->
          <el-button
            class="filter-toggle"
            :class="{
              'is-open': filtersOpen,
              'has-active': activeFilterCount > 0,
            }"
            size="small"
            @click="filtersOpen = !filtersOpen"
          >
            <el-icon><Filter /></el-icon>
            {{ filtersOpen ? "Hide Advanced Filters" : "Advanced Filters" }}
            <span v-if="activeFilterCount > 0" class="search-bar__badge">
              {{ activeFilterCount }}
            </span>
          </el-button>
          <el-button
            :disabled="!hasActiveFilters"
            size="small"
            plain
            @click="clearFilters"
          >
            Clear filters
          </el-button>
        </div>
      </div>

      <!-- Expandable Advanced filter panel -->
      <Transition name="filter-panel">
        <div v-if="filtersOpen" class="search-bar__filters">
          <!-- Activity Type + Status group -->
          <div class="filter-group-shaded filter-group-shaded--panel">
            <!-- Activity Type -->
            <div class="filter-group">
              <label class="filter-label">Activity Type</label>
              <el-select
                v-model="selectedActivityTypes"
                placeholder="All activity types"
                clearable
                size="small"
                multiple
                filterable
                collapse-tags
                collapse-tags-tooltip
                style="width: 220px"
              >
                <el-option
                  v-for="opt in activityTypeOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                  :disabled="opt.disabled"
                />
              </el-select>
            </div>

            <!-- Activity Status (grouped by workflow definition) -->
            <div v-if="statusLabels.length > 0" class="filter-group">
              <label class="filter-label">Activity Status</label>
              <el-select
                v-model="selectedStatuses"
                placeholder="All statuses"
                clearable
                multiple
                size="small"
                filterable
                collapse-tags
                collapse-tags-tooltip
                style="width: 220px"
              >
                <el-option-group
                  v-for="group in groupedStatuses"
                  :key="group.wdName"
                  :label="group.wdName"
                >
                  <el-option
                    v-for="status in group.statuses"
                    :key="status.code ?? status.label ?? ''"
                    :label="status.label ?? status.code ?? ''"
                    :value="status.code ?? ''"
                  />
                </el-option-group>
              </el-select>
            </div>
          </div>

          <div class="filter-panel-divider" />

          <!-- Date group-->
          <div class="filter-group-shaded filter-group-shaded--panel">
            <!-- Date Range -->
            <div class="filter-group">
              <label class="filter-label">Date Range</label>
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                size="small"
                :shortcuts="dateRangeShortcuts"
                range-separator="to"
                start-placeholder="Start date"
                end-placeholder="End date"
                style="width: 340px"
              />
            </div>

            <!-- Date Type-->
            <div class="filter-group">
              <label class="filter-label">Date Type</label>
              <el-select v-model="dateField" size="small" style="width: 180px">
                <el-option value="all" label="All Dates" />
                <el-option value="interaction" label="Interaction Date" />
                <el-option value="due" label="Due Date" />
                <el-option value="created" label="Create Date" />
              </el-select>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Result / chip bar — only after first search -->
      <div v-if="hasSearched" class="search-bar__results">
        <span class="results-count">
          Showing
          <strong>{{ items.length }}</strong>
          of
          <strong>{{ totalCount }}</strong>
          <span class="results-mode" :class="`results-mode--${lastSearchMode}`">
            <el-icon
              v-if="lastSearchMode === 'filtered'"
              class="results-mode__icon"
            >
              <Filter />
            </el-icon>
            {{ lastSearchMode === "all" ? "All Results" : "Filters Applied" }}
          </span>
          <span v-if="customPersonWarning" class="filters-hint">
            Select people to filter by
          </span>
        </span>

        <!-- Filters-ignored notice (visible when last search was "all" but filters exist) -->
        <span
          v-if="lastSearchMode === 'all' && hasActiveFilters"
          class="filters-ignored-notice"
        >
          Filters ignored.
          <button
            type="button"
            class="filters-ignored-notice__action"
            @click="() => searchActivities(true, 'filtered')"
          >
            Apply them
          </button>
        </span>

        <!-- Quick filter chip — always shown so user knows what scope was used -->
        <span
          class="active-chip active-chip--readonly"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
        >
          {{ quickFilterLabel }}
        </span>

        <!-- Include chip -->
        <span
          class="active-chip active-chip--readonly"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
        >
          {{ includeLabel }}
        </span>

        <!-- Custom user chips -->
        <button
          v-for="u in selectedUserLabels"
          :key="u.guid"
          type="button"
          class="active-chip"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
          :aria-label="`Remove person filter: ${u.label}`"
          @click="removeUser(u.guid)"
        >
          {{ u.label }}
          <span class="chip-x" aria-hidden="true">✕</span>
        </button>

        <!-- Activity type chips -->
        <button
          v-for="type in selectedActivityTypes"
          :key="type"
          type="button"
          class="active-chip"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
          :aria-label="`Remove filter: ${activityTypeOptions.find((o) => o.value === type)?.label ?? type}`"
          @click="removeActivityType(type)"
        >
          {{ activityTypeOptions.find((o) => o.value === type)?.label ?? type }}
          <span class="chip-x" aria-hidden="true">✕</span>
        </button>

        <!-- Status chips -->
        <button
          v-for="code in selectedStatuses"
          :key="code"
          type="button"
          class="active-chip"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
          :aria-label="`Remove filter: ${statusLabelByCode(code)}`"
          @click="removeStatus(code)"
        >
          {{ statusLabelByCode(code) }}
          <span class="chip-x" aria-hidden="true">✕</span>
        </button>

        <!-- Date range chip -->
        <button
          v-if="dateRangeLabel"
          type="button"
          class="active-chip"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
          aria-label="Remove date range filter"
          @click="removeDateRange"
        >
          {{ dateRangeLabel }}
          <span class="chip-x" aria-hidden="true">✕</span>
        </button>

        <!-- Date type chip — only shown when not "all" -->
        <button
          v-if="dateField !== 'all'"
          type="button"
          class="active-chip"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
          aria-label="Reset date type filter"
          @click="removeDateType"
        >
          {{ dateTypeLabel }}
          <span class="chip-x" aria-hidden="true">✕</span>
        </button>

        <div style="flex: 1" />

        <!-- Refresh button next to status -->
        <RefreshButton @click="refreshList" />
      </div>
    </div>

    <!-- Pagination -->
    <el-pagination
      v-if="hasSearched && totalCount > 0"
      class="activity-pagination"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="totalCount"
      :page-sizes="[25, 50, 100]"
      layout="total, sizes, prev, pager, next"
      @current-change="handlePageChange"
      @size-change="handleSizeChange"
    />

    <!-- Table area -->
    <div class="table-area">
      <ContactActivityTable
        :data="items"
        :status-list="statusList"
        :activity-type-options="activityTypeOptions"
        :loading="loading"
        :show-housing-application-column="true"
        @activity-click="showActivity"
      />
    </div>
  </div>

  <WorkflowResponseViewer
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :full-screen="false"
    :compliance-workflow="selectedRow"
    :focus-form-response-guid="focusFormResponseGuid"
    @close-drawer="closeDrawer"
    @update-workflow="workflowUpdated"
    @refresh-list="refreshList"
  />
</template>

<style lang="less">
.ContactActivityList {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .table-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .el-table {
      flex: 1;
    }
  }

  .activity-pagination {
    justify-content: flex-end;
    padding: 12px 0 0;
    flex-shrink: 0;
  }

  .empty-prompt {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // ── Search bar ──────────────────────────────────────────────
  .search-bar {
    border-radius: 8px;
    background: var(--el-bg-color, #fff);
    overflow: hidden;

    .search-with-filters {
      margin-left: 0;
    }

    &__main {
      display: flex;
      align-items: stretch;
      gap: 0;
      flex-wrap: wrap;
    }

    &__zone {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      padding: 7px 12px;
      position: relative;

      // Search zone
      &--search {
        background: var(--el-color-primary-light-9, #ecf5ff);
        border-bottom: 2px solid var(--el-color-primary-light-7, #c6e2ff);
        border-right: 1px solid var(--el-color-primary-light-7, #c6e2ff);
        flex-shrink: 0;
      }

      // Filters zone
      &--filters {
        background: var(--el-fill-color, #f0f2f5);
        border-bottom: 2px solid var(--el-border-color, #dcdfe6);
        flex: 1;
        min-width: 0;
      }
    }

    &__zone-label {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.07em;
      text-transform: uppercase;
      white-space: nowrap;
      flex-shrink: 0;
      padding-right: 8px;
      margin-right: 2px;
      align-self: center;
      height: 16px;
      line-height: 16px;
    }

    // Label text stays neutral for contrast (AA on the tinted fills);
    // the zone hue comes from the background and the divider accent.
    &__zone--search &__zone-label {
      color: var(--el-text-color-regular, #606266);
      border-right: 1px solid var(--el-color-primary-light-6, #a0cfff);
    }

    &__zone--filters &__zone-label {
      color: var(--el-text-color-regular, #606266);
      border-right: 1px solid var(--el-border-color, #dcdfe6);
    }

    &__input {
      border: none;
      background: transparent;
      outline: none;
      font-size: 13px;
      color: var(--el-text-color-primary);
      width: 100%;

      &::placeholder {
        color: var(--el-text-color-placeholder);
      }

      .search-bar__detected-type {
        font-size: 11px;
        font-weight: 500;
        color: var(--el-text-color-placeholder);
        white-space: nowrap;
        transition: color 0.15s;

        &[data-field="name"],
        &[data-field="email"],
        &[data-field="phone"],
        &[data-field="appnum"] {
          color: var(--el-color-primary);
        }
      }
    }

    &__divider {
      width: 1px;
      height: 22px;
      background: var(--el-border-color-lighter, #e4e7ed);
      flex-shrink: 0;
      margin: 0 2px;
    }

    &__quick-filter {
      flex-shrink: 0;

      .ep-radio-button__inner {
        border-color: transparent;
        box-shadow: none;
        font-size: 12px;
        padding: 0 12px;
        height: 28px;
        line-height: 28px;
      }
    }

    .filter-toggle {
      transition: all 0.15s;

      &.is-open {
        background: var(--el-color-primary-light-9);
        border-color: var(--el-color-primary-light-5);
        color: var(--el-color-primary);
      }

      &.has-active {
        background: var(--el-color-primary-light-9);
        border-color: var(--el-color-primary);
        color: var(--el-color-primary);
        font-weight: 500;
      }
    }

    &__badge {
      min-width: 16px;
      height: 16px;
      border-radius: 8px;
      padding: 0 4px;
      background: var(--el-color-primary-light-8);
      color: var(--el-color-primary);
      font-size: 11px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    // Expandable advanced filter panel
    &__filters {
      border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
      padding: 10px 12px;
      display: flex;
      align-items: flex-end;
      gap: 10px;
      flex-wrap: wrap;
      background: var(--el-fill-color-lighter, #fafafa);
    }

    // Result / chip bar
    &__results {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
      background: var(--el-fill-color-lighter, #fafafa);
      min-height: 36px;
    }
  }

  // Filter panel transition
  .filter-panel-enter-active,
  .filter-panel-leave-active {
    transition:
      max-height 0.2s ease,
      opacity 0.2s ease;
    max-height: 200px;
    overflow: hidden;
  }
  .filter-panel-enter-from,
  .filter-panel-leave-to {
    max-height: 0;
    opacity: 0;
  }

  // ── Inline filter group row (used in main bar) ──
  .filter-group-inline {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  // ── Shaded group container (used in main bar and panel) ──
  .filter-group-shaded {
    background: var(--el-fill-color-light, #f5f7fa);
    border: 1px solid var(--el-border-color-lighter, #e4e7ed);
    border-radius: 6px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    .el-radio-group {
      margin-left: 0;
    }

    // Panel variant — stacks filter groups side by side with a bit more room
    &--panel {
      align-items: flex-end;
      padding: 8px 12px;
      gap: 12px;
    }
  }

  .filter-inline-item {
    display: flex;
    align-items: center;
  }

  .select-warning .el-select__wrapper {
    box-shadow: 0 0 0 2px var(--el-color-primary-light-5);
  }

  // ── Filter panel internals ──────────────────────────────────
  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .filter-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--el-text-color-secondary);
    white-space: nowrap;
  }

  .filter-panel-divider {
    width: 1px;
    height: 56px;
    background: var(--el-border-color-lighter, #e4e7ed);
    align-self: flex-end;
    flex-shrink: 0;
    margin: 0 4px;
  }

  // ── Result chips ────────────────────────────────────────────
  .results-count {
    font-size: 12px;
    color: var(--el-text-color-secondary);
    white-space: nowrap;

    strong {
      color: var(--el-text-color-primary);
      font-weight: 600;
    }

    .filters-hint {
      font-size: 11px;
      color: var(--el-color-primary);
      font-weight: 500;
      font-style: italic;
      margin: 0 5px 0 8px;
    }
  }

  .results-mode {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 20px;
    padding: 0 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    margin-left: 6px;
    vertical-align: middle;
    border: 1px solid transparent;
  }

  .results-mode__icon {
    font-size: 12px;
  }

  .results-mode--filtered {
    background: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    border-color: var(--el-color-primary-light-5);
  }

  .results-mode--all {
    background: var(--el-fill-color);
    color: var(--el-text-color-regular);
    border-color: var(--el-border-color-lighter);
  }

  .active-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 8px;
    border-radius: 100px;
    font-size: 11px;
    font-family: inherit;
    cursor: pointer;
    background: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    border: 1px solid var(--el-color-primary-light-5);
    white-space: nowrap;

    &:hover {
      background: var(--el-color-primary-light-8);
    }

    &--readonly {
      cursor: default;
      background: var(--el-fill-color-light, #f5f7fa);
      color: var(--el-text-color-secondary);
      border-color: var(--el-border-color-lighter, #e4e7ed);

      &:hover {
        background: var(--el-fill-color-light, #f5f7fa);
      }
    }

    &--ignored {
      background: var(--el-fill-color);
      color: var(--el-text-color-placeholder);
      border-color: var(--el-border-color-lighter);
      border-style: dashed;
      text-decoration: line-through;
      text-decoration-color: var(--el-text-color-placeholder);

      &:hover {
        background: var(--el-fill-color-dark);
      }
    }
  }

  .filters-ignored-notice {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--el-color-warning);
    font-style: italic;
  }

  .filters-ignored-notice__action {
    background: none;
    border: none;
    padding: 0;
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
    font-size: 11px;
    font-family: inherit;
    font-weight: 600;

    &:hover {
      color: var(--el-color-primary-light-3);
    }

    .chip-x {
      opacity: 0.5;
      font-size: 10px;
      margin-left: 1px;
    }
  }
}

.homeMain:has(.ContactActivityList) {
  overflow: hidden;
}

// Mobile
@media (max-width: 768px) {
  .ContactActivityList {
    padding: 0.75rem;
    .search-bar {
      &__main {
        flex-wrap: wrap;
        height: auto;
        padding: 8px;
        gap: 6px;
      }

      &__filters {
        flex-direction: column;
        align-items: stretch;
      }

      .filter-group-shaded {
        flex-wrap: wrap;
      }
    }
  }
}
</style>
